/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import parse, { DOMNode, domToReact, Element } from 'html-react-parser';
import TabbedComponent from './TabbedComponent';
import StepGraphicsBlog from './StepGraphicsBlog';
import { stepGraphicsContent, IStepIdValue } from '../../content/step-graphics';
import EducationalAccordion from './EducationalAccordion';
import BlogAccordion from './BlogAccordion';
import {
  generateAccordion,
  generateTabContent,
  processCommonNodes,
  generateMeetExpertData
} from '../../helpers/single-post-replacements';
import QSWidget from './QSWidget';
import SGWidget from './SGWidget';
import SMWidget from './SMWidget';
import CustomEsmWidget from './CustomEsmWidget';
import MeetExpert, { IExpertCardProps } from './MeetExpert';
import {
  IContentGroup,
  PostContentParams,
  QSWidgetClassNames,
  IEducationalAccordion
} from '../../helpers/interfaces';
import { isCalculatorType, returnCalculatorType, calcMarkerId } from '../../helpers/calculators';
import { processContentWithTokens } from '../../helpers/functions';

interface PostContentProps {
  content: string;
  params: PostContentParams;
}
export function sanitizeAndCombineText(text: string) {
  // Remove HTML tags and special symbols
  const sanitizedText = text.replace(/<\/?[^>]+(>|$)/g, '').replace(/[^\w\s]/gi, '');
  // Combine words using hyphen
  return sanitizedText.split(/\s+/).join('-');
}
function PostContent({ content, params }: PostContentProps) {
  let accordionIndex = 0;
  let tabsTableIndex = 0;
  let eduAccordionIndex = 0;
  let meetExpertIndex = 0;

  // Function to replace html with corresponding React components
  const htmlToReact = (node: Element) => {
    // Find span.disc-info-icon and replace with disclosure icon
    const updatedNode = processCommonNodes(node, params);
    if (!(updatedNode instanceof Element)) {
      return updatedNode;
    }
    // Handle div#quinn widget if qsWidget isn't null
    if (params.qsWidget && node.name === 'div' && node.attribs?.id === 'quinn') {
      const postData = {
        id: params.postId ? params.postId.toString() : '',
        catSlug: params.catSlug ?? '',
        catId: params.categoryId ? params.categoryId.toString() : '',
        isSEMtemplate: params.isSEMtemplate || false
      };

      return (
        <QSWidget
          qsWidget={params.qsWidget}
          postData={postData}
          className={node.attribs.class as QSWidgetClassNames}
        />
      );
    }
    // Handle div#savingsgroup widget if SGWidget isn't null
    if (params.sgWidget && node.name === 'div' && node.attribs?.id === 'savingsgroup') {
      const postData = {
        id: params.postId ? params.postId.toString() : '',
        catSlug: params.catSlug ?? '',
        catId: params.categoryId ? params.categoryId.toString() : '',
        isSEMtemplate: params.isSEMtemplate || false
      };

      return (
        <SGWidget
          sgWidget={params.sgWidget}
          postData={postData}
          className={node.attribs.class as QSWidgetClassNames}
        />
      );
    }
    // Handle div#supermoney widget if SMWidget isn't null
    if (params.smWidget && node.name === 'div' && node.attribs?.id === 'supermoney') {
      const postData = {
        id: params.postId ? params.postId.toString() : '',
        catSlug: params.catSlug ?? '',
        catId: params.categoryId ? params.categoryId.toString() : '',
        isSEMtemplate: params.isSEMtemplate || false
      };
      return (
        <SMWidget
          smWidget={params.smWidget}
          postData={postData}
          className={node.attribs.class as QSWidgetClassNames}
        />
      );
    }
    // handle custom esm widget for checking account widget on landing page
    if (
      node.name === 'div' &&
      node.attribs?.id === 'quinn-esm' &&
      params.qsWidget &&
      params.qsWidget === 'checking' &&
      params.isSEMtemplate
    ) {
      return <CustomEsmWidget isSEMtemplate={params.isSEMtemplate} />;
    }

    // Handle Step CTAs if they're as divs with specific IDs
    if (
      node.name === 'div' &&
      node.attribs?.id &&
      Object.keys(stepGraphicsContent).includes(node.attribs.id)
    ) {
      const stepId = node.attribs.id as IStepIdValue;
      return (
        <StepGraphicsBlog
          id={stepId}
          data={stepGraphicsContent[stepId]}
          params={params}
        />
      );
    }
    // Handle accordion elements
    if (
      node.name === 'div' &&
      node.attribs?.class &&
      node.attribs.class.includes('blog-accordion')
    ) {
      // Get the corresponding accordion data
      const accordionData: IContentGroup[] = generateAccordion(node /* , params */);
      // Increment the accordion index for the next accordion
      accordionIndex++;
      if (accordionData?.length > 0) {
        return (
          <BlogAccordion
            data={accordionData}
            id={`${accordionIndex}-acc`}
          />
        );
      }
    }
    // Handle tabs table
    if (
      node.name === 'div' &&
      node.attribs?.class &&
      node.attribs.class.includes('table-nav-captioned')
    ) {
      const tabComponentTitle: any = node.children.find((child: any) => child.name === 'h3');
      const tabContentData: IContentGroup[] = generateTabContent(node /* , params */);

      tabsTableIndex++;
      return (
        <TabbedComponent
          content={tabContentData}
          tabTitle={tabComponentTitle ? domToReact(tabComponentTitle.children).toString() : ''}
          tabId={`tab-table-${tabsTableIndex}`}
        />
      );
    }
    // Handle EducationalAccordion elements
    if (
      node.name === 'div' &&
      node.attribs?.class &&
      node.attribs.class.includes('educational-accordion')
    ) {
      // Init eduAccordion data
      const eduAccordionData: IEducationalAccordion = {
        title: '',
        lead: '',
        content: '',
        id: `eduAccordion-${eduAccordionIndex++}`
      };
      const eduAccordionChildren = node.children;
      if (eduAccordionChildren.length > 0) {
        eduAccordionChildren.forEach((child: any) => {
          if (child.name === 'h3') {
            eduAccordionData.title = child.children[0].data;
          } else if (child.name === 'p') {
            eduAccordionData.lead = child.children[0].data;
          } else if (child.name === 'ul') {
            const cont = ReactDOMServer.renderToStaticMarkup(domToReact([child]));
            eduAccordionData.content = parse(cont, {
              replace: (nodeChild: DOMNode) =>
                processCommonNodes(nodeChild as Element /* , params */)
            });
          }
        });
      }
      return <EducationalAccordion data={eduAccordionData} />;
    }
    // handle calculators calc-embed
    if (
      node.name === 'div' &&
      node.attribs?.id &&
      node.attribs.id === calcMarkerId &&
      params.calculator &&
      isCalculatorType(params.calculator)
    ) {
      return returnCalculatorType(params.calculator, params.isFullCalculator ?? false);
    }

    // Handle MeetExpert elements
    if (node.name === 'div' && node.attribs?.class && node.attribs.class.includes('meet-expert')) {
      const meetExpertData = generateMeetExpertData(node);
      // Add id to the meet expert data
      meetExpertData.id = `expert-${meetExpertIndex++}`;
      return <MeetExpert {...meetExpertData} />;
    }

    // If no replacements, return the node
    return node;
  };

  let processedContent = content;
  // Process any Looker tokens in the content
  if (params.lookerData) {
    processedContent = processContentWithTokens(processedContent, params.lookerData);
  }

  // Parse and replace elements within the content
  const renderedContent = parse(processedContent, {
    replace: (node: DOMNode) => htmlToReact(node as Element)
  });

  return renderedContent;
}

export default PostContent;
